import styled from "styled-components";

// Shared design kit for project detail pages.
// Theme: brand red #F34452, dark text #333, font Raleway.

export const ProjectContainer = styled.div`
  padding: 50px;
  color: #727272;
  font-family: "Raleway", sans-serif;
`;

export const ProjectTitle = styled.div`
  margin-bottom: 24px;

  h2 {
    font-size: 28px;
    color: #333;
    margin: 0 0 8px 0;
    font-weight: 700;
  }

  .tags {
    color: #f34452;
    font-weight: 600;
    margin: 0;
  }
`;

export const Tagline = styled.p`
  border-left: 4px solid #f34452;
  padding: 4px 0 4px 18px;
  margin: 0 0 32px 0;
  font-size: 18px;
  line-height: 28px;
  color: #333;
  font-weight: 500;
`;

export const MetricsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StatCard = styled.div`
  background: #fafafa;
  border: 1px solid #eee;
  border-top: 3px solid #f34452;
  border-radius: 4px;
  padding: 18px 14px;
  text-align: center;

  .value {
    display: block;
    font-size: 26px;
    font-weight: 700;
    color: #f34452;
    line-height: 1.1;
  }

  .label {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
`;

export const ProjectDescription = styled.div`
  margin-bottom: 28px;
  line-height: 26px;
  font-size: 15px;
`;

export const ProjectInfo = styled.div`
  margin-bottom: 36px;

  p {
    margin: 8px 0;

    b {
      color: #333;
    }

    a {
      color: #f34452;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export const SectionTitle = styled.h4`
  font-size: 20px;
  color: #333;
  margin: 0 0 18px 0;
  font-weight: 600;
`;

export const Footnote = styled.p`
  font-size: 13px;
  color: #888;
  line-height: 22px;
  margin: 0 0 16px 0;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;

  span {
    background: #fafafa;
    border: 1px solid #e2e2e2;
    border-radius: 20px;
    padding: 8px 16px;
    font-size: 13px;
    color: #444;
    font-weight: 500;
  }
`;

export const ProjectWrapper = styled.div`
  margin-bottom: 40px;

  ul {
    list-style: none;
    line-height: 24px;
    margin: 0;
    padding: 0;

    li {
      position: relative;
      padding-left: 22px;
      margin-bottom: 12px;
      font-size: 14px;
      line-height: 22px;

      b {
        color: #333;
      }
    }

    li::before {
      content: "▸";
      position: absolute;
      left: 0;
      top: 0;
      color: #f34452;
      font-weight: 700;
    }
  }

  img {
    width: 100%;
    max-width: 1000px;
    height: auto;
    margin: 20px auto 0 auto;
    display: block;
    border: 1px solid #eee;
    border-radius: 4px;
  }
`;

export const VideoEmbed = styled.div`
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 20px auto 0 auto;
  aspect-ratio: 16 / 9;
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    position: relative;
    padding-left: 26px;
    margin-bottom: 10px;
    font-size: 14px;
    line-height: 22px;
  }

  li::before {
    content: "✓";
    position: absolute;
    left: 0;
    top: 0;
    color: #28a745;
    font-weight: 700;
  }
`;

export const Caption = styled.p`
  font-size: 12px;
  color: #999;
  text-align: center;
  font-style: italic;
  margin: 8px 0 0 0;
`;

export const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: #eee;
  margin: 30px 0;
`;

export const ProjectNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 0px;
  padding: 10px;
  border-bottom: 1px solid #eee;

  .spacer {
    flex: 1;
  }

  .nav-link {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    padding: 6px 4px;
    cursor: pointer;
    text-align: left;
    transition: all 0.25s ease;

    &.next {
      flex-direction: row-reverse;
      text-align: right;
    }

    > span {
      flex: 1;
      min-width: 0;
    }

    .label {
      display: block;
      font-size: 0.65em;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      color: #aaa;
      margin-bottom: 2px;
      transition: color 0.25s;
    }

    .title {
      display: block;
      font-size: 0.9em;
      font-weight: 600;
      color: #555;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color 0.25s;
    }

    i {
      font-size: 1em;
      color: #bbb;
      transition:
        transform 0.25s ease,
        color 0.25s;
    }

    &:hover {
      .label,
      .title,
      i {
        color: #f34452;
      }
    }

    &.prev:hover i {
      transform: translateX(-4px);
    }

    &.next:hover i {
      transform: translateX(4px);
    }
  }

  .back {
    align-self: center;
    background: none;
    border: 2px solid transparent;
    cursor: pointer;
    color: #444;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 10px 20px;
    transition: all 0.3s;

    i {
      margin-right: 8px;
    }

    &:hover {
      border-color: #f34452;
      color: #f34452;
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
    gap: 28px;

    .spacer {
      display: none;
    }

    .nav-link {
      flex: 0 0 auto;
      padding: 6px;
    }

    .nav-link > span {
      display: none;
    }

    .nav-link i {
      font-size: 1.4em;
    }

    .back {
      padding: 6px;

      i {
        margin-right: 0;
        font-size: 1.3em;
      }
    }

    .back-label {
      display: none;
    }

    .back:hover {
      border-color: transparent;
      color: #f34452;
    }
  }
`;

// Two-column comparison cards (e.g. RAG vs CAG). $primary = the highlighted choice.
export const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const CompareCard = styled.div<{ $primary?: boolean }>`
  border: 1px solid ${(p) => (p.$primary ? "#F34452" : "#e2e2e2")};
  background: ${(p) => (p.$primary ? "#fff5f6" : "#fafafa")};
  border-radius: 6px;
  padding: 20px;

  .head {
    font-size: 15px;
    font-weight: 700;
    color: ${(p) => (p.$primary ? "#F34452" : "#999")};
    margin: 0 0 14px 0;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 14px 0;

    li {
      position: relative;
      padding-left: 24px;
      margin-bottom: 10px;
      line-height: 22px;
      font-size: 14px;
    }

    li::before {
      content: "${(p) => (p.$primary ? "✓" : "✕")}";
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 700;
      color: ${(p) => (p.$primary ? "#28a745" : "#c0392b")};
    }
  }

  .verdict {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(p) => (p.$primary ? "#28a745" : "#c0392b")};
  }
`;

// Horizontal stage flow (e.g. UI → API → DB).
export const FlowDiagram = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const FlowBox = styled.div`
  flex: 1;
  background: #fafafa;
  border: 1px solid #e2e2e2;
  border-left: 3px solid #f34452;
  border-radius: 4px;
  padding: 14px;

  .name {
    font-weight: 700;
    color: #333;
    font-size: 15px;
    margin-bottom: 6px;
  }

  .sub {
    font-size: 12px;
    color: #888;
    line-height: 18px;
  }
`;

export const FlowArrow = styled.div`
  display: flex;
  align-items: center;
  color: #f34452;
  font-weight: 700;
  font-size: 22px;

  @media (max-width: 768px) {
    justify-content: center;
    transform: rotate(90deg);
  }
`;
